/**
 * LmsStandaloneQuizzes — list & create standalone quizzes (not attached to any course).
 * Public share links + guest sign-in supported via the Detail page.
 */
import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { apiErrorMessage } from "@/lib/apiError";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { HelpCircle, Plus, ShieldAlert, Link2, Users } from "lucide-react";

export default function LmsStandaloneQuizzes() {
  const { activeWorkspaceId } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    title: "", description: "", scored: true, pass_percent: 70,
    time_limit_min: "", prometric_mode: "strict", max_attempts: 1,
  });

  const load = useCallback(async () => {
    if (!activeWorkspaceId) return;
    try {
      const { data } = await api.get(`/workspaces/${activeWorkspaceId}/lms/quizzes/standalone`);
      setRows(Array.isArray(data) ? data : []);
    } catch (e) { toast.error(apiErrorMessage(e, "Load failed")); }
  }, [activeWorkspaceId]);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!form.title.trim()) return toast.error("Title is required");
    try {
      const payload = { ...form, time_limit_min: form.time_limit_min ? Number(form.time_limit_min) : null };
      const { data } = await api.post(`/workspaces/${activeWorkspaceId}/lms/quizzes`, payload);
      setOpen(false);
      navigate(`/app/lms/standalone-quizzes/${data.id}`);
    } catch (e) { toast.error(apiErrorMessage(e, "Create failed")); }
  };

  return (
    <div className="p-6 space-y-4">
      <PageHeader
        title="Standalone Quizzes"
        description="Independent assessments — sharable via public link + guest sign-in"
        actions={
          <Button className="rounded-none h-9" onClick={() => setOpen(true)} data-testid="stdq-new">
            <Plus className="w-4 h-4 mr-1" /> New quiz
          </Button>
        }
      />
      {rows.length === 0 && (
        <div className="border border-dashed border-border p-8 text-center">
          <HelpCircle className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
          <p className="text-sm text-muted-foreground">No standalone quizzes yet.</p>
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {rows.map((q) => (
          <div key={q.id} className="border border-border p-4 hover:border-primary cursor-pointer" onClick={() => navigate(`/app/lms/standalone-quizzes/${q.id}`)} data-testid={`stdq-card-${q.id}`}>
            <div className="flex items-start gap-2">
              <HelpCircle className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-sm truncate">{q.title}</div>
                <div className="text-[10px] uppercase text-muted-foreground mt-0.5 flex gap-2">
                  <span className="chip chip-info text-[9px]">{q.prometric_mode}</span>
                  {q.scored && <span className="chip text-[9px]">SCORED · {q.pass_percent}%</span>}
                  {q.time_limit_min && <span className="chip text-[9px]">{q.time_limit_min}m</span>}
                </div>
              </div>
              <ShieldAlert className="w-4 h-4 text-amber-500" />
            </div>
            {q.description && <p className="text-xs text-muted-foreground line-clamp-2 mt-2">{q.description}</p>}
            <div className="flex items-center gap-3 mt-3 text-[11px] text-muted-foreground">
              <span>{q.question_count || 0} questions</span>
              <span className="inline-flex items-center gap-1"><Users className="w-3 h-3" /> {q.attempt_count || 0} attempts</span>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md rounded-none" data-testid="stdq-new-dialog">
          <DialogHeader><DialogTitle>New standalone quiz</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <div>
              <Label className="text-xs mb-1 block">Title *</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="h-9 rounded-none" data-testid="stdq-new-title" />
            </div>
            <div>
              <Label className="text-xs mb-1 block">Description</Label>
              <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="h-9 rounded-none" />
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div>
                <Label className="text-[10px] label-eyebrow">Mode</Label>
                <Select value={form.prometric_mode} onValueChange={(v) => setForm({ ...form, prometric_mode: v })}>
                  <SelectTrigger className="h-9 rounded-none text-xs" data-testid="stdq-new-mode"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="strict">Strict</SelectItem>
                    <SelectItem value="moderate">Moderate</SelectItem>
                    <SelectItem value="off">Off</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[10px] label-eyebrow">Time limit (min)</Label>
                <Input type="number" min={1} value={form.time_limit_min} onChange={(e) => setForm({ ...form, time_limit_min: e.target.value })} className="h-9 rounded-none" placeholder="—" />
              </div>
              <div>
                <Label className="text-[10px] label-eyebrow">Pass %</Label>
                <Input type="number" min={1} max={100} value={form.pass_percent} onChange={(e) => setForm({ ...form, pass_percent: Number(e.target.value) })} className="h-9 rounded-none" />
              </div>
            </div>
            <label className="inline-flex items-center gap-2 text-xs">
              <Switch checked={form.scored} onCheckedChange={(v) => setForm({ ...form, scored: v })} /> Scored
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-none" onClick={() => setOpen(false)}>Cancel</Button>
            <Button className="rounded-none" onClick={create} data-testid="stdq-new-create">Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
